/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';

const Params = z.strictObject({
  organizationId: ZodTypes.Id,
  projectId: ZodTypes.Id,
});

export const PostCluster = {
  id: 'cluster.post',
  tag: 'Cluster',
  summary: 'Create cluster',
  description: 'Create a cluster within an organization project',
  method: 'post',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) =>
    `/organization/${params.organizationId}/project/${params.projectId}/cluster`,
  request: {
    params: Params,
    body: {
      required: true,
      content: {
        'application/json': {
          schema: z.strictObject({
            service: z.discriminatedUnion('type', [
              z.strictObject({ type: z.literal('babelfish') }),
              z.strictObject({ type: z.literal('cypex') }),
              z.strictObject({ type: z.literal('migrator') }),
              z.strictObject({ type: z.literal('pgee') }),
              z.strictObject({ type: z.literal('postgres') }),
            ]),
            restore: z
              .strictObject({
                organizationId: ZodTypes.Id,
                projectId: ZodTypes.Id,
                clusterId: ZodTypes.Id,
                timestamp: ZodTypes.Timestamp,
                cloneAcl: z.boolean().optional(),
                cloneParameters: z.boolean().optional(),
                cloneLogicalBackupConfiguration: z.boolean().optional(),
                cloneContinuousBackupConfiguration: z.boolean().optional(),
              })
              .optional(),
            name: ZodTypes.TrimmedString.min(1),
            description: ZodTypes.TrimmedString.min(1).optional(),
            replicas: z.number().int().min(0),
            supportPlanId: ZodTypes.Id,
            storageClassId: z.string(),
            cpuUnits: z.number().min(0.25),
            memoryGbs: z.number().int().min(1),
            storageGbs: z.number().int().min(1),
            allowedSourceRanges: z.array(ZodTypes.IpAndRange).nullable(),
            backupConfiguration: z
              .strictObject({
                logical: z
                  .strictObject({
                    enabled: z.boolean().optional(),
                    schedule: z
                      .string()
                      .regex(/^(\d+|\*)(\/\d+)?(\s+(\d+|\*)(\/\d+)?){4}$/)
                      .optional(),
                    daysToRetain: z.number().positive().optional(),
                  })
                  .optional(),
                continuous: z
                  .strictObject({
                    enabled: z.boolean().optional(),
                    schedule: z
                      .string()
                      .regex(/^(\d+|\*)(\/\d+)?(\s+(\d+|\*)(\/\d+)?){4}$/)
                      .optional(),
                    daysToRetain: z.number().positive().optional(),
                  })
                  .optional(),
              })
              .optional(),
            maintenanceWindows: z.array(z.string()).optional(),
            datasets: z
              .array(
                z.strictObject({
                  datasetId: ZodTypes.Id,
                  database: ZodTypes.NoSpacesString,
                  readonly: z.boolean(),
                }),
              )
              .optional(),
            postgresVersion: z.number(),
          }),
        },
      },
    },
  },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: z.strictObject({
            id: ZodTypes.Id,
            type: ZodTypes.ServiceType,
            name: z.string().min(1),
            description: z.string().min(1).nullable().optional(),
            createdAt: z.date(),
            updatedAt: z.date(),
            configuration: z.array(
              z.strictObject({
                id: ZodTypes.Id,
                createdAt: z.date(),
                replicas: z.number().min(0),
                endedAt: z.date().or(z.literal('infinity')),
                cpuUnits: z.number().min(0),
                memoryGbs: z.number().min(0),
                storageGbs: z.number().min(0),
                postgresVersion: z.number().min(0),
                storageClassId: z.string().min(1),
                supportPlan: z.strictObject({
                  id: ZodTypes.Id,
                  name: z.string().min(1),
                  description: z.string().min(1),
                  isDefault: z.boolean(),
                  price: z.number(),
                }),
                storageClass: z.strictObject({
                  id: z.string().min(1),
                  isDefault: z.boolean(),
                }),
              }),
            ),
            isCloning: z.boolean(),
          }),
        },
      },
    },
  },
} as const satisfies Endpoint;
